import * as React from "react";
import styled, { ThemeProvider, css } from "styled-components";

import Hover from "../../common/components/Hover";
import Icon from "../../common/components/Icon";
import Image, { Image as ImageType } from "../../common/components/Image";
import { media, truncateLine } from "../../common/utils/style";
import { ToolTipWrapper, StyledFilledStarIcon, HandlerIconWrapper, OptionalProps } from "./MembershipCard";
import Translate from "../../common/components/Translate";
import Tooltip from "../../common/components/popover/Tooltip";

type FeaturedCompanyMembership = WtdModels.Partial<
  WtdModels.CompanyMembership,
  "position",
  {
    user: WtdModels.Partial<
      WtdModels.User,
      "name" | "user_path",
      {
        avatar: WtdModels.Union<WtdModels.Partial<WtdModels.Image, "id">, ImageType>;
        profile: WtdModels.Partial<WtdModels.Profile, "position">;
      }
    >;
  }
>;

export interface FeaturedMembershipCardModels {
  CompanyMembership: FeaturedCompanyMembership;
}

type Props = {
  membership: FeaturedCompanyMembership;
  lineColor: string | null;
} & OptionalProps;

class FeaturedMembershipCard extends React.PureComponent<Props> {
  public onClickMembership = (event: React.MouseEvent<HTMLElement>) => {
    if (this.props.editable) {
      event.preventDefault();
    }
  };

  public onTouchMembership = (event: React.TouchEvent<HTMLElement>) => {
    const { onMouseEnter, membership, isEntered } = this.props;
    if (onMouseEnter && !isEntered) {
      // TODO:(kdnk) Remove any
      onMouseEnter(membership as any);
      event.preventDefault();
    }
  };

  public onMouseEnterMembership = () => {
    const { onMouseEnter, membership, isEntered } = this.props;
    if (onMouseEnter && !isEntered) {
      // TODO:(kdnk) Remove any
      onMouseEnter(membership as any);
    }
  };

  public onClickStar = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const { isFeatured, membership, onFeatured, onUnfeatured } = this.props;
    if (isFeatured) {
      if (onUnfeatured) {
        // TODO:(kdnk) Remove any
        onUnfeatured(membership as any);
      }
    } else {
      if (onFeatured) {
        // TODO:(kdnk) Remove any
        onFeatured(membership as any);
      }
    }
  };

  public render() {
    const { user, position } = this.props.membership;
    if (!user) {
      return null;
    }
    const { editable, isEntered } = this.props;
    return (
      <ThemeProvider theme={{ ...this.props }}>
        <Hover>
          {({ isHovered, ref }) => (
            <Base
              innerRef={ref}
              href={user.user_path}
              onMouseEnter={this.onMouseEnterMembership}
              onMouseUp={this.onClickMembership}
              onTouchEnd={this.onTouchMembership}
              onClick={this.onClickMembership}
            >
              <AvatarWrapper>
                <Avatar>
                  <AvatarImageCover
                    image={user.avatar}
                    size="120x120#"
                    alt={user.name}
                    draggable={false}
                    lazy={false}
                    background
                  />
                </Avatar>
              </AvatarWrapper>
              {isEntered ? (
                <SelectedLine />
              ) : (
                <MemberDescriptor>
                  <Name>{user.name}</Name>
                  <Position>{position}</Position>
                </MemberDescriptor>
              )}
              {editable && (
                <>
                  <ToolTipWrapper>
                    <Tooltip render={<Translate id="company.members.remove_from_home" />}>
                      <StyledFilledStarIcon filled={true} onClick={this.onClickStar} />
                    </Tooltip>
                  </ToolTipWrapper>
                  {isHovered && (
                    <HandlerIconWrapper>
                      <Icon name="wt-icon-bers" />
                    </HandlerIconWrapper>
                  )}
                </>
              )}
            </Base>
          )}
        </Hover>
      </ThemeProvider>
    );
  }
}

export default FeaturedMembershipCard;

const Base = styled.a`
  position: relative;
  display: block;
  width: 100%;
  max-width: 180px;
  margin: 0 auto;
  padding-bottom: 19px;
  border-radius: 10px;
  text-align: center;
  border: 1px solid white;
  cursor: ${props => (props.theme.editable ? "move" : "pointer")};
  &:hover {
    text-decoration: none;
  }
  @media ${media.medium} {
    padding: 20px 0 29px 0;
  }
  ${props =>
    props.theme.editable &&
    css`
      transition: background-color 150ms ease-out;
      &:hover {
        background-color: rgba(245, 245, 245, 1);
      }
    `};
`;

const AvatarWrapper = styled.div`
  position: relative;
  margin: 0 auto 8px auto;
  width: 70%;
  max-width: 110px;
  @media ${media.large} {
    margin: 0 auto 19px auto;
  }
  &:before {
    content: "";
    display: block;
    padding-top: 100%;
  }
`;

// [NOTE(kobayang)] A tricky solution that height equals width with pure css
// http://www.mademyday.de/css-height-equals-width-with-pure-css.html
const Avatar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
`;

const AvatarImageCover = styled(Image)`
  border-radius: 50%;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: transform 150ms ease-out;
  ${props =>
    props.theme.isEntered &&
    css`
      transform: scale(1.06);
      @media ${media.medium} {
        transform: scale(1.27);
      }
    `};
`;

const MemberDescriptor = styled.div`
  padding: 0 5px;
  margin-top: 6px;
  text-align: center;
`;

const Name = styled.div`
  display: none;
  font-weight: 600;
  line-height: 1.17;
  color: rgba(0, 0, 0, 0.8);
  font-size: 18px;
  @media ${media.medium} {
    display: block;
    font-size: 14px;
  }
`;

const Position = styled.div`
  font-size: 11px;
  font-weight: 300;
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.4);
  ${truncateLine};
  @media ${media.medium} {
    font-size: 13px;
    font-weight: 400;
  }
`;

const SelectedLine = styled.div`
  border-radius: 1.5px;
  background-color: ${props => props.theme.lineColor};
  margin: 19px auto 0 auto;
  width: 12%;
  height: 2px;
  @media ${media.medium} {
    margin: 50px auto 0 auto;
    width: 29px;
    height: 3px;
  }
`;



// WEBPACK FOOTER //
// ./frontend/src/company_memberships/components/FeaturedMembershipCard.tsx